import { memo } from 'react'
import Link from 'next/link'
import { cx } from 'class-variance-authority'
import { ArrowDownLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowDownLeft'
import { ArrowUpRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { Badge } from '@skop-app/lib-front'
import { formatPrice } from '@skop-app/lib-domain/utils'

type UserStatisticsListProps = {
  cards: {
    id: string
    name: string
    total: number | string
    icon?: React.ElementType
    amounts?: {
      name: string
      currency?: string
      value: number
    }[]
    link?: string
    linkMessage?: string
    trend: {
      progression: number
      evolution: number
      highIsBad?: boolean
      iconTrend?: boolean
    }
  }[]
  threeItems?: boolean
}

export const UserStatisticsList = memo(
  ({ cards, threeItems = false }: UserStatisticsListProps) => (
    <ul
      className={cx({
        'grid gap-4': true,
        'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3': threeItems,
        'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4':
          !threeItems,
      })}
    >
      {cards.map((card) => (
        <li
          key={card.id}
          className="overflow-hidden rounded-xl border border-zinc-100 shadow-sm"
        >
          <div className="flex h-auto items-start gap-x-4 bg-white p-4">
            <div className="flex size-12 flex-none items-center justify-center rounded-lg border border-orange-500 bg-orange-400 object-cover">
              {card.icon ? (
                <card.icon className="size-6 text-slate-300" />
              ) : (
                <div className="size-6 rounded bg-slate-300" />
              )}
            </div>
            <div className="text-sm font-medium text-gray-900">
              <p className="text-sm text-slate-100">{card.name}</p>
              <div className="flex w-full items-end">
                <p className="line-clamp-1 text-2xl">{card.total}</p>
                <ProgressPill {...card.trend} />
              </div>
              {card?.amounts?.map((amount, index) => {
                return (
                  <div
                    key={amount.name ?? index}
                    className="flex w-full items-end"
                  >
                    <p className="mt-2 text-sm text-slate-300">
                      {formatPrice({
                        amount: amount.value,
                        currency: amount.currency ?? 'EUR',
                      })}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
          {!!card?.link && (
            <div className="bg-zinc-50 px-6 py-4 text-sm">
              <Link
                href={card.link}
                className="block cursor-pointer text-slate-200 underline-offset-2 hover:text-slate-500 hover:underline"
              >
                {card.linkMessage}
              </Link>
            </div>
          )}
        </li>
      ))}
    </ul>
  ),
)

UserStatisticsList.displayName = 'UserStatisticsList'

type ProgressPillProps = {
  progression: number
  evolution: number
  highIsBad?: boolean
  iconTrend?: boolean
}

const ProgressPill = ({
  progression,
  evolution,
  highIsBad,
  iconTrend,
}: ProgressPillProps) => {
  const pillStyle = cx(
    iconTrend ? 'px-0.5 py-0.5' : 'px-2 py-1',
    'ml-2 mb-0.5 text-xs font-semibold rounded-full',
  )

  const progressionLabel = (progression: number) => {
    if (progression === 0) return '-'
    return progression > 0 ? `+${progression}` : progression
  }

  if (progression > 0) {
    return (
      <Badge
        variant={!highIsBad ? 'lightSuccess' : 'lightDestructive'}
        className={cx('mb-0.5 ml-1.5', pillStyle)}
      >
        {iconTrend ? (
          <ArrowUpRightIcon className="inline-block size-5" />
        ) : (
          `${progressionLabel(progression)} (${evolution}%)`
        )}
      </Badge>
    )
  } else if (progression < 0) {
    return (
      <Badge
        variant={highIsBad ? 'lightSuccess' : 'lightDestructive'}
        className={cx('mb-0.5 ml-1.5', pillStyle)}
      >
        {iconTrend ? (
          <ArrowDownLeftIcon className="inline-block size-5" />
        ) : (
          `${progressionLabel(progression)} (${evolution}%)`
        )}
      </Badge>
    )
  } else {
    return (
      <Badge variant="outline" className="mb-1 ml-1.5 bg-zinc-50">
        {iconTrend ? '--' : progressionLabel(progression)}
      </Badge>
    )
  }
}
